import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 12;

const PaymentSuccessPage = () => {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", info: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", info: data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid" && data.status === "complete") {
          setState({ status: "failed", info: data });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setState({ status: "timeout", info: data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts.current >= MAX_POLLS) {
          setState({ status: "failed", info: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const currency = new Intl.NumberFormat(lang === "pt" ? "pt-PT" : "en-US", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  });

  return (
    <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-20" data-testid="payment-success-page">
        <div className="max-w-lg w-full bg-[#FCFAF8] border border-[#0A1128]/10 p-10 md:p-14 text-center">
          {state.status === "checking" && (
            <>
              <Loader2 size={40} strokeWidth={1.2} className="mx-auto text-[#C5A059] animate-spin" />
              <div className="overline mt-8">{lang === "pt" ? "A confirmar pagamento" : "Confirming payment"}</div>
              <h1 className="font-serif-display text-3xl md:text-4xl font-light text-[#0A1128] mt-3">
                {lang === "pt" ? "Um momento…" : "One moment…"}
              </h1>
              <p className="mt-4 text-[#3A435E] font-light">
                {lang === "pt"
                  ? "Estamos a validar o seu pagamento junto da Stripe."
                  : "We're validating your payment with Stripe."}
              </p>
            </>
          )}

          {state.status === "paid" && (
            <>
              <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-[#2D6A4F]" />
              <div className="overline mt-8 text-[#2D6A4F]">{lang === "pt" ? "Pagamento recebido" : "Payment received"}</div>
              <h1 className="font-serif-display text-4xl md:text-5xl font-light text-[#0A1128] mt-3">
                {t.reserve.success}
              </h1>
              {state.info?.metadata && (
                <div className="mt-8 text-left border-t border-[#0A1128]/10 pt-6 space-y-2 text-sm text-[#3A435E]">
                  <div className="flex justify-between">
                    <span className="overline">{lang === "pt" ? "Casa" : "House"}</span>
                    <span className="text-[#0A1128]">{state.info.metadata.house_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="overline">Check-in</span>
                    <span className="text-[#0A1128]">{state.info.metadata.check_in}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="overline">Check-out</span>
                    <span className="text-[#0A1128]">{state.info.metadata.check_out}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="overline">{lang === "pt" ? "Hóspedes" : "Guests"}</span>
                    <span className="text-[#0A1128]">{state.info.metadata.guests_count}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-4 pt-4 border-t border-[#0A1128]/10">
                    <span className="overline">{t.reserve.total}</span>
                    <span className="font-serif-display text-3xl text-[#C5A059]">
                      {currency.format((state.info.amount_total || 0) / 100)}
                    </span>
                  </div>
                </div>
              )}
              <Link
                to="/"
                className="mt-10 inline-flex items-center gap-2 bg-[#0A1128] text-white hover:bg-[#162243] px-8 py-4 text-xs tracking-[0.22em] uppercase"
                data-testid="payment-back-home"
              >
                {lang === "pt" ? "Voltar ao início" : "Back to home"}
              </Link>
            </>
          )}

          {(state.status === "failed" || state.status === "timeout") && (
            <>
              <AlertTriangle size={40} strokeWidth={1.2} className="mx-auto text-[#9B2226]" />
              <div className="overline mt-8 text-[#9B2226]">{lang === "pt" ? "Pagamento não confirmado" : "Payment not confirmed"}</div>
              <h1 className="font-serif-display text-3xl md:text-4xl font-light text-[#0A1128] mt-3">
                {lang === "pt"
                  ? "Não conseguimos validar o seu pagamento."
                  : "We couldn't confirm your payment."}
              </h1>
              <p className="mt-4 text-[#3A435E] font-light">
                {lang === "pt"
                  ? "Verifique a sua caixa de entrada ou tente novamente. Se o valor foi debitado, contacte-nos."
                  : "Please check your inbox or try again. If you were charged, contact us."}
              </p>
              <Link
                to="/#houses"
                className="mt-8 inline-flex items-center gap-2 border border-[#0A1128] px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#0A1128] hover:text-white transition-colors"
              >
                {lang === "pt" ? "Voltar ao portfólio" : "Back to portfolio"}
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
